import { Controller } from "@hotwired/stimulus"
import { moveSelection, selectCurrent, syncSelectionFromFocus } from "../lib/result_keyboard_navigation"
import { highlightMatch } from "../lib/search_highlight"

export default class extends Controller {
  static targets = [
    "input",
    "tab",
    "count",
    "results",
    "resultsHeader",
    "resultsTitle",
    "resultsCount",
    "empty",
    "noResults",
    "loading",
    "loadingMore",
    "tabsDrawer",
    "filtersDrawer",
    "backdrop",
    "filterPanel",
    "clearButton",
    "scrollContainer"
  ]
  static values = {
    query: String,
    type: String,
  }

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handlePopState = this.handlePopState.bind(this)
    this.interceptShortcut = this.interceptShortcut.bind(this)
    this.handleScroll = this.handleScroll.bind(this)
    document.addEventListener("keydown", this.handleKeydown)
    document.addEventListener("keydown", this.interceptShortcut, true)
    window.addEventListener("popstate", this.handlePopState)

    this.debounceTimer = null
    this.currentFetch = null
    this._selectedIndex = -1
    this.currentPage = 0
    this.totalResults = 0
    this.isLoadingMore = false

    if (this.hasScrollContainerTarget) {
      this.scrollContainerTarget.addEventListener("scroll", this.handleScroll)
    }

    if (this.hasResultsTarget) {
      this.resultsTarget.addEventListener("focusin", (e) => {
        this._selectedIndex = syncSelectionFromFocus(this.resultsTarget, e)
      })
    }

    if (this.hasInputTarget) {
      this.inputTarget.focus()
      this.inputTarget.selectionStart = this.inputTarget.selectionEnd = this.inputTarget.value.length
    }

    this.updateCompaniesTabVisibility()

    if (this.queryValue.length > 0) {
      this.fetchResults()
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown)
    document.removeEventListener("keydown", this.interceptShortcut, true)
    window.removeEventListener("popstate", this.handlePopState)
    if (this.hasScrollContainerTarget) {
      this.scrollContainerTarget.removeEventListener("scroll", this.handleScroll)
    }
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    if (this.currentFetch) this.currentFetch.abort()
    this.closeDrawers()
  }

  interceptShortcut(event) {
    const target = event.target
    const typing = target && (
      target.tagName === "INPUT" ||
      target.tagName === "TEXTAREA" ||
      target.isContentEditable
    )
    if (typing) return

    if (event.key === "?" && event.shiftKey) {
      event.preventDefault()
      event.stopPropagation()
      if (this.hasInputTarget) this.inputTarget.focus()
      return
    }

    const GLOBAL_SHORTCUT_KEYS = ["/", "p", "P", "c", "C", "a", "A", "k", "K", "w", "W"]
    if (!GLOBAL_SHORTCUT_KEYS.includes(event.key)) return
    if (event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return

    event.preventDefault()
    event.stopPropagation()

    if (event.key === "/" && this.hasInputTarget) {
      this.inputTarget.focus()
    }
  }

  search() {
    this.queryValue = this.inputTarget.value
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => {
      this.updateUrl(false)
      this.fetchResults()
    }, 150)
  }

  applyFilters() {
    if (this.isSyncing) return
    this.syncFiltersAcrossPanels()
    this.updateUrl(false)
    this.updateCompaniesTabVisibility()
    this.fetchResults()
  }

  updateCompaniesTabVisibility() {
    const companyTab = this.tabTargets.find((tab) => tab.dataset.type === "Company")
    if (!companyTab) return

    const params = new URLSearchParams(window.location.search)
    const hasCompanyFilter = !!params.get("company")

    companyTab.classList.toggle("d-none", hasCompanyFilter)

    if (hasCompanyFilter && this.typeValue === "Company") {
      const firstVisibleTab = this.tabTargets.find((tab) => !tab.classList.contains("d-none"))
      if (firstVisibleTab) {
        this.typeValue = firstVisibleTab.dataset.type
        this.updateActiveTab()
        this.updateFilterPanelVisibility()
        this.updateUrl(false)
      }
    }
  }

  syncFiltersAcrossPanels() {
    const activePanel = this.filterPanelTargets.find((p) => !p.hidden)
    if (!activePanel) return

    this.isSyncing = true

    const activeValues = {}
    activePanel.querySelectorAll('input[type="checkbox"], select').forEach((el) => {
      const name = this.filterNameFor(el)
      if (!name) return

      if (el.type === "checkbox") {
        activeValues[name] = { type: "checkbox", value: el.checked }
      } else if (el.multiple) {
        activeValues[name] = { type: "multi", value: Array.from(el.selectedOptions).map((o) => o.value).filter(Boolean) }
      } else {
        activeValues[name] = { type: "single", value: el.value }
      }
    })

    this.filterPanelTargets.forEach((panel) => {
      if (panel === activePanel) return

      panel.querySelectorAll('input[type="checkbox"], select').forEach((el) => {
        const name = this.filterNameFor(el)
        if (!name || !(name in activeValues)) return
        const entry = activeValues[name]

        if (entry.type === "checkbox") {
          el.checked = entry.value
        } else if (entry.type === "multi" && el.tomselect) {
          el.tomselect.setValue(entry.value)
        } else if (entry.type === "multi") {
          Array.from(el.options).forEach((opt) => { opt.selected = entry.value.includes(opt.value) })
        } else {
          el.value = entry.value
        }
      })
    })

    this.isSyncing = false
  }

  clearFilters(event) {
    if (event) event.preventDefault()
    const activePanel = this.filterPanelTargets.find((p) => !p.hidden)
    if (!activePanel) return

    activePanel.querySelectorAll('input[type="checkbox"]').forEach((el) => {
      el.checked = false
    })

    activePanel.querySelectorAll('select').forEach((el) => {
      if (el.tomselect) {
        el.tomselect.clear()
      } else {
        el.value = ""
      }
    })
    this.syncFiltersAcrossPanels()
    this.updateUrl(false)
    this.updateCompaniesTabVisibility()
    this.fetchResults()
  }

  selectTab(event) {
    event.preventDefault()
    const tab = event.currentTarget
    const type = tab.dataset.type

    this.typeValue = type
    this.updateActiveTab()
    this.updateFilterPanelVisibility()
    this.updateUrl(true)
    this.scrollResultsToTop()
    this.fetchTabResults()
    this.closeDrawers()
  }

  updateFilterPanelVisibility() {
    this.filterPanelTargets.forEach((panel) => {
      panel.hidden = panel.dataset.filterPanelFor !== this.typeValue
    })
  }

  scrollResultsToTop() {
    if (this.hasScrollContainerTarget) this.scrollContainerTarget.scrollTop = 0
  }

  updateActiveTab() {
    this.tabTargets.forEach((tab) => {
      const isActive = tab.dataset.type === this.typeValue
      tab.classList.toggle("advanced-search__tab--active", isActive)
    })
  }

  updateUrl(pushHistory) {
    const params = new URLSearchParams()
    if (this.queryValue) params.set("q", this.queryValue)
    if (this.typeValue) params.set("type", this.typeValue)

    const filterParams = this.collectFilterParams()
    Object.entries(filterParams).forEach(([key, value]) => params.set(key, value))

    const url = `${window.location.pathname}${params.toString() ? `?${params}` : ""}`
    const state = { q: this.queryValue, type: this.typeValue, filters: filterParams }

    if (pushHistory) {
      window.history.pushState(state, "", url)
    } else {
      window.history.replaceState(state, "", url)
    }
  }

  collectFilterParams() {
    const params = {}
    const activePanel = this.filterPanelTargets.find((p) => !p.hidden)
    if (!activePanel) return params

    activePanel.querySelectorAll('input[type="checkbox"], select').forEach((el) => {
      const name = this.filterNameFor(el)
      if (!name) return

      if (el.type === "checkbox") {
        if (el.checked) params[name] = "true"
      } else if (el.multiple) {
        const selected = Array.from(el.selectedOptions).map((o) => o.value).filter(Boolean)
        if (selected.length > 0) params[name] = selected.join(",")
      } else if (el.value) {
        params[name] = el.value
      }
    })
    return params
  }

  filterNameFor(el) {
    if (el.dataset.filterName) return el.dataset.filterName
    if (!el.name) return null
    const match = el.name.match(/filters\[([^\]]+)\]/)
    return match ? match[1] : el.name
  }

  handlePopState(event) {
    const state = event.state || {}
    this.queryValue = state.q || ""
    this.typeValue = state.type || this.typeValue
    if (this.hasInputTarget) this.inputTarget.value = this.queryValue
    this.updateActiveTab()
    this.updateFilterPanelVisibility()
    this.hydrateFiltersFromState(state.filters || {})
    this.fetchResults()
  }

  hydrateFiltersFromState(filterState) {
    this.filterPanelTargets.forEach((panel) => {
      panel.querySelectorAll('input[type="checkbox"], select').forEach((el) => {
        const name = this.filterNameFor(el)
        if (!name) return
        const value = filterState[name]

        if (el.type === "checkbox") {
          el.checked = value === "true"
        } else if (el.tomselect) {
          el.tomselect.setValue(value ? value.split(",") : [])
        } else {
          el.value = value || ""
        }
      })
    })
  }

  buildSearchParams() {
    const params = new URLSearchParams({ q: this.queryValue, format: "json" })
    const filterParams = this.collectFilterParams()
    Object.entries(filterParams).forEach(([key, value]) => {
      if (key === "comprehensive") {
        params.set("search_by_all", value)
      } else {
        params.set(key, value)
      }
    })
    return params
  }

  fetchResults() {
    if (!this.queryValue || this.queryValue.trim().length < 3) {
      this.showEmpty()
      this.resetCounts()
      return
    }

    if (this.currentFetch) this.currentFetch.abort()
    const controller = new AbortController()
    this.currentFetch = controller

    this.showLoading()
    this.currentPage = 1
    this.totalResults = 0

    const params = this.buildSearchParams()
    params.set("consolidated_results", "true")
    params.set("type", this.typeValue)
    params.set("page", "1")

    fetch(`/searches?${params}`, {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error("Search request failed")
        return response.json()
      })
      .then((data) => {
        if (this.currentFetch !== controller) return
        this.updateCounts(data)
        this.renderResults(data)
      })
      .catch((error) => {
        if (error.name === "AbortError") return
        this.hideLoading()
      })
      .finally(() => {
        if (this.currentFetch === controller) this.currentFetch = null
      })
  }

  fetchTabResults() {
    if (!this.queryValue || this.queryValue.trim().length < 3) return

    if (this.currentFetch) this.currentFetch.abort()
    const controller = new AbortController()
    this.currentFetch = controller

    this.showLoading()
    this.currentPage = 1
    this.totalResults = 0

    const params = this.buildSearchParams()
    params.set("type", this.typeValue)
    params.set("page", "1")

    fetch(`/searches?${params}`, {
      headers: { Accept: "application/json" },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error("Search request failed")
        return response.json()
      })
      .then((data) => {
        if (this.currentFetch !== controller) return
        this.renderPagedResults(data.results || [], data.total || 0)
      })
      .catch((error) => {
        if (error.name === "AbortError") return
        this.hideLoading()
      })
      .finally(() => {
        if (this.currentFetch === controller) this.currentFetch = null
      })
  }

  handleScroll() {
    if (this.isLoadingMore || !this.hasMoreResults()) return

    const el = this.hasScrollContainerTarget ? this.scrollContainerTarget : this.resultsTarget
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
      this.loadMore()
    }
  }

  hasMoreResults() {
    const loaded = this.resultsTarget.querySelectorAll(".searcher__result").length
    return loaded < this.totalResults
  }

  loadMore() {
    if (this.isLoadingMore || !this.hasMoreResults()) return
    this.isLoadingMore = true
    this.currentPage++

    if (this.hasLoadingMoreTarget) this.loadingMoreTarget.hidden = false

    const params = this.buildSearchParams()
    params.set("type", this.typeValue)
    params.set("page", String(this.currentPage))

    fetch(`/searches?${params}`, {
      headers: { Accept: "application/json" },
    })
      .then((response) => {
        if (!response.ok) throw new Error("Load more failed")
        return response.json()
      })
      .then((data) => {
        this.appendResults(data.results || [])
      })
      .catch(() => {
        this.currentPage--
      })
      .finally(() => {
        this.isLoadingMore = false
        if (this.hasLoadingMoreTarget) this.loadingMoreTarget.hidden = true
      })
  }

  updateCounts(data) {
    const pluralToSingular = this.typeKeyMap()
    this.countTargets.forEach((countEl) => {
      const type = countEl.dataset.type
      const pluralKey = Object.keys(pluralToSingular).find((k) => pluralToSingular[k] === type)
      const section = pluralKey ? data[pluralKey] : null
      countEl.textContent = section ? section.total : "0"
    })
  }

  resetCounts() {
    this.countTargets.forEach((countEl) => {
      countEl.textContent = "0"
    })
  }

  renderResults(data) {
    const pluralToSingular = this.typeKeyMap()
    const pluralKey = Object.keys(pluralToSingular).find((k) => pluralToSingular[k] === this.typeValue)
    const section = pluralKey ? data[pluralKey] : null
    const results = section?.results || []
    const total = section?.total || 0

    this.renderPagedResults(results, total)
  }

  renderPagedResults(results, total) {
    this.hideLoading()
    this.totalResults = total
    this.updateResultsHeader(total)

    if (results.length === 0) {
      this.showNoResults()
      return
    }

    this.hideEmpty()
    this.hideNoResults()

    const fallbackIcon = this.iconForType(this.typeValue)
    const html = results
      .map((result) => this.buildRowHtml(result, fallbackIcon, this.typeValue))
      .join("")

    const container = document.createElement("div")
    container.className = "searcher__type-section"
    container.innerHTML = `<div class="searcher__type-results">${html}</div>`

    this.resultsTarget.replaceChildren(container)
    this.resultsTarget.classList.remove("advanced-search__results--loading")
  }

  appendResults(results) {
    if (results.length === 0) return

    const container = this.resultsTarget.querySelector(".searcher__type-results")
    if (!container) return

    const fallbackIcon = this.iconForType(this.typeValue)
    const html = results
      .map((result) => this.buildRowHtml(result, fallbackIcon, this.typeValue))
      .join("")

    container.insertAdjacentHTML("beforeend", html)
  }

  updateResultsHeader(total) {
    if (!this.hasResultsHeaderTarget) return

    if (this.hasResultsTitleTarget) {
      this.resultsTitleTarget.textContent = this.activeTabLabel()
    }
    if (this.hasResultsCountTarget) {
      this.resultsCountTarget.textContent = `${total} ${total === 1 ? "result" : "results"}`
    }
    this.resultsHeaderTarget.hidden = false
  }

  hideResultsHeader() {
    if (this.hasResultsHeaderTarget) this.resultsHeaderTarget.hidden = true
  }

  activeTabLabel() {
    const activeTab = this.tabTargets.find((tab) => tab.dataset.type === this.typeValue)
    return activeTab?.dataset.label || this.typeValue
  }

  buildRowHtml(result, fallbackIcon, type) {
    const icon = result.icon || fallbackIcon
    const iconStyle =
      result.icon_color && result.icon_background_color
        ? ` style="color: ${this.escape(result.icon_color)}; background-color: ${this.escape(result.icon_background_color)};"`
        : ""
    const url = result.url || "#"
    const query = this.queryValue
    const name = highlightMatch(this.escape(result.name || ""), query)

    let secondary = ""
    let rightLabel = ""

    switch (type) {
      case "Company":
        secondary = result.nickname ? `<p>${highlightMatch(this.escape(result.nickname), query)}</p>` : ""
        break
      case "Asset":
        secondary = result.asset_type ? `<p>${this.escape(result.asset_type)}</p>` : ""
        rightLabel = result.company_name ? highlightMatch(this.escape(result.company_name), query) : ""
        break
      case "Password":
        secondary = result.folder_path ? `<p><i class="fas fa-folder"></i> ${this.escape(result.folder_path)}</p>` : ""
        rightLabel = result.company_name ? highlightMatch(this.escape(result.company_name), query) : ""
        break
      case "Article":
        secondary = result.folder_path ? `<p><i class="fas fa-folder"></i> ${this.escape(result.folder_path)}</p>` : ""
        rightLabel = result.company_name ? highlightMatch(this.escape(result.company_name), query) : "Central KB"
        break
      case "Process":
      case "Website":
        rightLabel = result.company_name ? highlightMatch(this.escape(result.company_name), query) : ""
        break
    }

    return `
      <a href="${this.escape(url)}" class="searcher__result" target="_top">
        <main>
          <i class="searcher__result-icon ${this.escape(icon)}"${iconStyle}></i>
          <div class="searcher__result_content">
            <h2>${name}</h2>
            ${secondary}
          </div>
        </main>
        <span>${rightLabel}</span>
      </a>
    `
  }

  escape(value) {
    const div = document.createElement("div")
    div.textContent = String(value ?? "")
    return div.innerHTML
  }

  iconForType(type) {
    const icons = {
      Company: "far fa-briefcase",
      Asset: "far fa-circle",
      Password: "far fa-key",
      Article: "far fa-book-reader",
      Process: "far fa-check",
      Website: "far fa-globe",
    }
    return icons[type] || "far fa-circle"
  }

  typeKeyMap() {
    return {
      companies: "Company",
      assets: "Asset",
      passwords: "Password",
      articles: "Article",
      processes: "Process",
      websites: "Website",
    }
  }

  showLoading() {
    this.hideEmpty()
    this.hideNoResults()
    if (this.hasLoadingTarget) this.loadingTarget.hidden = false
    this.resultsTarget.classList.add("advanced-search__results--loading")
  }

  hideLoading() {
    if (this.hasLoadingTarget) this.loadingTarget.hidden = true
    this.resultsTarget.classList.remove("advanced-search__results--loading")
  }

  showEmpty() {
    this.hideLoading()
    this.hideNoResults()
    this.hideResultsHeader()
    this.resultsTarget.replaceChildren()
    if (this.hasEmptyTarget) this.emptyTarget.hidden = false
  }

  hideEmpty() {
    if (this.hasEmptyTarget) this.emptyTarget.hidden = true
  }

  showNoResults() {
    this.hideLoading()
    this.hideEmpty()
    this.hideResultsHeader()
    this.resultsTarget.replaceChildren()
    if (this.hasNoResultsTarget) this.noResultsTarget.hidden = false
  }

  hideNoResults() {
    if (this.hasNoResultsTarget) this.noResultsTarget.hidden = true
  }

  openTabs(event) {
    event.preventDefault()
    this.closeDrawers()
    this.tabsDrawerTarget.classList.add("advanced-search__drawer--open")
    this.showBackdrop()
  }

  openFilters(event) {
    event.preventDefault()
    this.closeDrawers()
    this.filtersDrawerTarget.classList.add("advanced-search__drawer--open")
    this.showBackdrop()
  }

  closeDrawers() {
    if (this.hasTabsDrawerTarget) {
      this.tabsDrawerTarget.classList.remove("advanced-search__drawer--open")
    }
    if (this.hasFiltersDrawerTarget) {
      this.filtersDrawerTarget.classList.remove("advanced-search__drawer--open")
    }
    this.hideBackdrop()
  }

  showBackdrop() {
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.add("advanced-search__backdrop--visible")
    }
  }

  hideBackdrop() {
    if (this.hasBackdropTarget) {
      this.backdropTarget.classList.remove("advanced-search__backdrop--visible")
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.anyDrawerOpen()) {
      this.closeDrawers()
      return
    }

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this._selectedIndex = moveSelection(this.resultsTarget, this._selectedIndex, 1)
        break
      case "ArrowUp":
        event.preventDefault()
        this._selectedIndex = moveSelection(this.resultsTarget, this._selectedIndex, -1)
        break
      case "Enter":
        if (this._selectedIndex >= 0) {
          event.preventDefault()
          selectCurrent(this.resultsTarget, this._selectedIndex)
        }
        break
    }
  }

  anyDrawerOpen() {
    return (
      (this.hasTabsDrawerTarget && this.tabsDrawerTarget.classList.contains("advanced-search__drawer--open")) ||
      (this.hasFiltersDrawerTarget && this.filtersDrawerTarget.classList.contains("advanced-search__drawer--open"))
    )
  }
}